import {
  CardFeedCard,
  CardFeedCardStatus,
} from 'src/queries/cardFeeds/cardFeeds.models'

export type CardFeedSortField =
  | 'cardName'
  | 'cardHolder'
  | 'status'
  | 'dateAdded'

/**
 * Drives all sort/filter/search for the card-feed table. Field names mirror the
 * eventual server request shape (cf. VirtualCardsFiltersBody:
 * search / statuses / sortField / sortDirection) so a future move to
 * server-side filtering is a localized swap — see the design spec.
 */
export interface CardFeedCardsView {
  search?: string
  statuses?: CardFeedCardStatus[]
  sortField?: CardFeedSortField
  sortDirection?: 'ASC' | 'DESC'
}

// Logical ordering for the Status column — not alphabetical. Unassigned first
// (actionable), then Pending, then Assigned (done).
const STATUS_RANK: Record<CardFeedCardStatus, number> = {
  UNASSIGNED: 0,
  PENDING: 1,
  ASSIGNED: 2,
}

const matchesSearch = (card: CardFeedCard, term: string): boolean => {
  const haystack = [card.cardName, card.lastFour, card.cardHolder ?? '']
    .join(' ')
    .toLowerCase()
  return haystack.includes(term)
}

const compareByField = (
  a: CardFeedCard,
  b: CardFeedCard,
  field: CardFeedSortField,
): number => {
  switch (field) {
    case 'dateAdded':
      return new Date(a.dateAdded).getTime() - new Date(b.dateAdded).getTime()
    case 'status':
      return STATUS_RANK[a.status] - STATUS_RANK[b.status]
    case 'cardHolder': {
      // Empty/null cardholder sorts last in ascending order.
      const av = a.cardHolder ?? '￿'
      const bv = b.cardHolder ?? '￿'
      return av.toLowerCase().localeCompare(bv.toLowerCase())
    }
    case 'cardName':
    default:
      return a.cardName.toLowerCase().localeCompare(b.cardName.toLowerCase())
  }
}

export const selectVisibleCards = (
  cards: CardFeedCard[],
  view: CardFeedCardsView,
): CardFeedCard[] => {
  let result = cards

  const term = view.search?.trim().toLowerCase()
  if (term) {
    result = result.filter((card) => matchesSearch(card, term))
  }

  if (view.statuses && view.statuses.length > 0) {
    const allowed = new Set(view.statuses)
    result = result.filter((card) => allowed.has(card.status))
  }

  if (view.sortField) {
    const direction = view.sortDirection === 'DESC' ? -1 : 1
    // Copy before sort — never mutate the caller's array (React Query cache).
    result = [...result].sort(
      (a, b) =>
        compareByField(a, b, view.sortField as CardFeedSortField) * direction,
    )
  }

  return result
}
